import {
  BellRing,
  Bike,
  Gift,
  Hand,
  Image,
  LayoutGrid,
  Newspaper,
  Palette,
  Scale,
  Share2,
  ShoppingBag,
  ShoppingBasket,
  Compass,
  Smartphone,
  Store,
  Target,
  UserPlus,
  MessageSquareQuote,
  MousePointerClick,
  Tag,
  Type,
  UserRound,
  UsersRound,
  Utensils
} from 'lucide-vue-next'

import type { Component } from 'vue'
import i18n from '@/translations'
import {
  BRAND_APP_ORDER_CHANNELS,
  type BrandAppOrderChannelId,
  type BrandAppTabId,
  type HomeBlockId
} from './brandAppContract'
import type { RegistrationStepId } from './brandAppRegistration'
import type { PreviewZone } from '../composables/webappPageTabs'

export type BrandAppSection = {
  id: string
  name: string
  description: string
  icon: Component
}

export const HERO_SECTION: BrandAppSection = {
  id: 'hero',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.hero.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.hero.description')
  },
  icon: Image
}

export const HOME_BLOCK_SECTIONS: Record<HomeBlockId, BrandAppSection> = {
  offers: {
    id: 'offers',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.offers.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.offers.description')
    },
    icon: Tag
  },
  news: {
    id: 'news',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.news.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.news.description')
    },
    icon: Newspaper
  },
  menu: {
    id: 'menu',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.menu.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.menu.description')
    },
    icon: Utensils
  },
  social: {
    id: 'social',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.social.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.social.description')
    },
    icon: Share2
  }
}

export const SCREEN_SECTIONS: Record<Exclude<BrandAppTabId, 'home'>, BrandAppSection> = {
  order: {
    id: 'order',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.order.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.order.description')
    },
    icon: ShoppingBasket
  },
  loyalty: {
    id: 'loyalty',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.loyalty.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.loyalty.description')
    },
    icon: Gift
  },
  stores: {
    id: 'stores',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.stores.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.stores.description')
    },
    icon: Store
  }
}

export const STORE_ACTIONS_SECTION: BrandAppSection = {
  id: 'stores-actions',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.storesActions.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.storesActions.description')
  },
  icon: MousePointerClick
}

export const STORE_LIST_SECTION: BrandAppSection = {
  id: 'stores-list',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.storesList.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.storesList.description')
  },
  icon: Store
}

export const STORES_SCREEN_SECTIONS = [STORE_ACTIONS_SECTION, STORE_LIST_SECTION]

const ORDER_CHANNEL_ICONS: Record<BrandAppOrderChannelId, Component> = {
  clickAndCollect: ShoppingBag,
  uberEats: Bike,
  deliveroo: Bike,
  onSite: Utensils
}

export const ORDER_CHANNEL_SECTIONS: (BrandAppSection & { channel: BrandAppOrderChannelId })[] =
  BRAND_APP_ORDER_CHANNELS.map((channel) => ({
    id: `order-${channel.id}`,
    get name() {
      return channel.label
    },
    get description() {
      return channel.urlLabel
    },
    icon: ORDER_CHANNEL_ICONS[channel.id],
    channel: channel.id
  }))

export const orderChannelForSection = (id: string): BrandAppOrderChannelId | null =>
  ORDER_CHANNEL_SECTIONS.find((section) => section.id === id)?.channel ?? null

export const LOYALTY_IMAGES_SECTION: BrandAppSection = {
  id: 'loyalty-images',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.loyaltyImages.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.loyaltyImages.description')
  },
  icon: Image
}

export const REGISTRATION_SECTION: BrandAppSection = {
  id: 'registration',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.registration.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.registration.description')
  },
  icon: UserPlus
}

export const MISSIONS_SECTION: BrandAppSection = {
  id: 'missions',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.missions.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.missions.description')
  },
  icon: Target
}

export const AUDIENCE_RULES_SECTION: BrandAppSection = {
  id: 'audience-rules',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.audienceRules.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.audienceRules.description')
  },
  icon: UsersRound
}

export const RECOVERY_SECTION: BrandAppSection = {
  id: 'recovery',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.recovery.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.recovery.description')
  },
  icon: Gift
}

export const REGISTRATION_STEP_SECTIONS: Record<RegistrationStepId, BrandAppSection> = {
  'reg-welcome': {
    id: 'reg-welcome',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regWelcome.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regWelcome.description')
    },
    icon: Hand
  },
  'reg-phone': {
    id: 'reg-phone',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regPhone.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regPhone.description')
    },
    icon: Smartphone
  },
  'reg-store': {
    id: 'reg-store',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regStore.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regStore.description')
    },
    icon: Store
  },
  'reg-profile': {
    id: 'reg-profile',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regProfile.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regProfile.description')
    },
    icon: UserRound
  },
  'reg-optin': {
    id: 'reg-optin',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regOptin.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.regOptin.description')
    },
    icon: BellRing
  }
}

export const REGISTRATION_STEPS = Object.values(REGISTRATION_STEP_SECTIONS)

export const loyaltyScreenSections = (hasLoyaltyTab: boolean): BrandAppSection[] =>
  hasLoyaltyTab
    ? [LOYALTY_IMAGES_SECTION, REGISTRATION_SECTION, MISSIONS_SECTION, AUDIENCE_RULES_SECTION, RECOVERY_SECTION]
    : [LOYALTY_IMAGES_SECTION, REGISTRATION_SECTION]

const PARENT_SECTION: Record<string, string> = Object.fromEntries(
  REGISTRATION_STEPS.map((step) => [step.id, REGISTRATION_SECTION.id])
)

export const parentSection = (id: string): string | null => PARENT_SECTION[id] ?? null

const PREVIEW_ZONE_BY_SECTION: Record<string, PreviewZone> = {
  'loyalty-images': 'home',
  registration: 'home',
  'reg-welcome': 'home',
  'reg-phone': 'phone',
  'reg-store': 'storeSelection',
  'reg-profile': 'profileInfo',
  'reg-optin': 'optin',
  missions: 'loyalty',
  'audience-rules': 'loyalty',
  recovery: 'loyalty',
  legal: 'storeSelection'
}

export const previewZoneForSection = (id: string): PreviewZone | undefined => PREVIEW_ZONE_BY_SECTION[id]

export const DESIGN_SECTIONS: BrandAppSection[] = [
  {
    id: 'design-brand',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designBrand.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designBrand.description')
    },
    icon: Image
  },
  {
    id: 'design-colors',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designColors.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designColors.description')
    },
    icon: Palette
  },
  {
    id: 'design-typography',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designTypography.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designTypography.description')
    },
    icon: Type
  },
  {
    id: 'design-voice',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designVoice.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.designVoice.description')
    },
    icon: MessageSquareQuote
  }
]

export const NAVIGATION_SECTION: BrandAppSection = {
  id: 'navigation',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.navigation.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.navigation.description')
  },
  icon: LayoutGrid
}

export const HOME_LAYOUT_SECTION: BrandAppSection = {
  id: 'home',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.home.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.home.description')
  },
  icon: Compass
}

export const LOYALTY_LAYOUT_SECTION: BrandAppSection = {
  id: 'loyalty-layout',
  get name() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.loyaltyLayout.name')
  },
  get description() {
    return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.loyaltyLayout.description')
  },
  icon: Gift
}

export const SETTINGS_SECTIONS: BrandAppSection[] = [
  {
    id: 'legal',
    get name() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.legal.name')
    },
    get description() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.sections.legal.description')
    },
    icon: Scale
  }
]

const ALL_SECTIONS: BrandAppSection[] = [
  HERO_SECTION,
  ...Object.values(HOME_BLOCK_SECTIONS),
  ...Object.values(SCREEN_SECTIONS),
  ...ORDER_CHANNEL_SECTIONS,
  ...STORES_SCREEN_SECTIONS,
  LOYALTY_IMAGES_SECTION,
  REGISTRATION_SECTION,
  ...REGISTRATION_STEPS,
  MISSIONS_SECTION,
  AUDIENCE_RULES_SECTION,
  RECOVERY_SECTION,
  ...DESIGN_SECTIONS,
  NAVIGATION_SECTION,
  HOME_LAYOUT_SECTION,
  LOYALTY_LAYOUT_SECTION,
  ...SETTINGS_SECTIONS
]

export const findSection = (id: string): BrandAppSection | undefined =>
  ALL_SECTIONS.find((section) => section.id === id)

export type PanelTab = 'text' | 'content'

const TEXT_TAB_SECTION_IDS = ['offers', 'news', 'menu', 'social', 'loyalty', 'reg-profile']

const TEXT_LABEL_SECTION_IDS = ['loyalty', 'reg-profile']

export const hasTextTab = (id: string): boolean => TEXT_TAB_SECTION_IDS.includes(id)

export const textTabLabel = (id: string): string =>
  i18n.global.t(
    `pages.loyaltySupports.webapp.editor.sections.textTab.${TEXT_LABEL_SECTION_IDS.includes(id) ? 'text' : 'title'}`
  )

const PREVIEW_ONLY_SECTION_IDS = new Set(['order', 'stores', 'stores-actions', 'stores-list'])

export const COMING_SOON_SCREENS: BrandAppTabId[] = ['order', 'stores']

export const isPreviewOnlySection = (id: string): boolean =>
  PREVIEW_ONLY_SECTION_IDS.has(id) || orderChannelForSection(id) !== null
